import React from "react";
import { connect } from "react-redux";
import { createSelector, defaultMemoize, createSelectorCreator } from "reselect";
import { isEqual, keys, pick } from "../../utils/lodash";
import { QuestionModel } from "../../models/QuestionModel";
import { StoreState } from "../../models/states/StoreState";
import { ControlProps } from "../../components/controls/BaseControl";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { Signal } from "../../signals/index";
import ErrorRenderer from "../../components/shared/ErrorRenderer";
import {
    randomizeVariables,
    randomizeRows,
    randomizeColumns,
} from "../../randomization/Randomization";
import { LogicModel } from "../../models/LogicModel";
import { __evaluateSafe__ } from "../../expressions/parser";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { NavigationType } from "../../models/enums/NavigationType";
import {
    getCurrentBlock,
    getCurrentLoop,
} from "../../redux/reducers/interview/navigationUtilities";
import { Piping } from "../../piping";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { eliminateVariables, eliminateRows, eliminateColumns } from "../../elimination/Elimination";

interface Props {
    question: QuestionModel;
    controlType: any;
    gridRendering?: boolean;
    isSingleOnPage?: boolean;
    validateSignal?: Signal<ValidationOptionsModel, ValidationResultModel>;
    showSoftPrompt?: boolean;
    onNavigation?(type: NavigationType): void;
    isDisabled?: boolean;
    carrouselActiveVariableIndex?: number;
    onAfterSelection?(): void;
}

interface State {
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

class ControlContainerImpl extends React.PureComponent<ControlProps & Props, State> {
    constructor(props: ControlProps & Props) {
        super(props);
        this.state = {};
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const { question } = this.props;
        // Clear error.
        if (question !== nextProps.question) this.setState({ error: undefined });
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // Display fallback UI
        this.setState({ error });
    }

    render() {
        const { error } = this.state;
        if (error) {
            return <ErrorRenderer error={error} />;
        }
        const { controlType } = this.props;
        return React.createElement(controlType, {
            ...this.props,
        });
    }
}

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const createShallowEqualSelector = createSelectorCreator(defaultMemoize, (a, b) => {
    if (a === b) return true;

    if (!a || !b) return false;

    const aKeys = keys(a);
    const bKeys = keys(b);
    const aKL = aKeys.length;
    const bKL = bKeys.length;

    if (aKL !== bKL) return false;

    for (let i = 0; i < aKL; i++) {
        const k = aKeys[i];
        if (k !== bKeys[i] || a[k] !== b[k]) return false;
    }

    return true;
});

const interviewRef = { current: undefined };
const staticallySafeEvaluator = (logic: LogicModel) =>
    __evaluateSafe__(interviewRef.current, logic).asBoolean();
const getStaticallySafeEvaluator = () => staticallySafeEvaluator;

// Wrap mapStateToProps to create selectors for every instance of ControlContainer
// as createSelector has a cache size of 1.
const makeMapStateToProps = () => {
    const getRespondent = (state: StoreState) => state.interview.respondent;
    const getQuestion = (_state: StoreState, props: Props) => props.question;
    const getVariableNames = (_state: StoreState, props: Props) => props.question.variableNames;
    const getVariableDependencies = (_state: StoreState, props: Props) =>
        props.question.variableDependencies;
    const getVariables = (_state: StoreState, props: Props) => props.question.variables;
    const getQuestionnaire = (state: StoreState) => state.interview.questionnaire;
    const getServerPage = (state: StoreState) => state.interview.navigation?.currentPageOnServer;
    const getLanguage = (state: StoreState) => state.interview.language;
    const getMentionsByVariableName = (state: StoreState) =>
        state.interview.variables.mentionsByVariableName;
    const getFilesByVariableName = (state: StoreState) => state.files.filesByVariableName;
    const getEliminationOptions = (_state: StoreState, props: Props) => ({
        includeInvisible: false,
        includeSoftPrompt: props.showSoftPrompt,
    });

    // Get the main mentions, dependencies' mentions and files.
    const getMentions = createSelector(
        [getVariableNames, getMentionsByVariableName],
        (vars, mentions) => pick(mentions, vars),
    );
    const getDependenciesMentions = createSelector(
        [getVariableNames, getVariableDependencies, getMentionsByVariableName],
        (vars, deps, mentions) => pick(mentions, [...vars, ...deps]),
    );
    const getFiles = createSelector([getVariableNames, getFilesByVariableName], (vars, files) =>
        pick(files, vars),
    );
    // Only return a new object if they are different.
    const getCachedMentions = createShallowEqualSelector(getMentions, (mentions) => mentions);
    const getCachedDependenciesMentions = createShallowEqualSelector(
        getDependenciesMentions,
        (mentions) => mentions,
    );
    const getCachedFiles = createShallowEqualSelector(getFiles, (files) => files);
    // Randomization.
    const getRandomizedVariables = createSelector(
        [getRespondent, getQuestionnaire, getQuestion, getVariables, getLanguage, getServerPage],
        randomizeVariables,
    );
    const getRandomizedRows = createSelector(
        [getRespondent, getQuestionnaire, getQuestion, getLanguage, getServerPage],
        randomizeRows,
    );
    const getRandomizedColumns = createSelector(
        [getRespondent, getQuestionnaire, getQuestion, getLanguage, getServerPage],
        randomizeColumns,
    );
    // Elimination.
    const getSequencedVariables = createSelector(
        [
            getQuestionnaire,
            getQuestion,
            getCachedDependenciesMentions,
            getRandomizedVariables,
            getStaticallySafeEvaluator,
            getEliminationOptions,
            getServerPage,
        ],
        eliminateVariables,
    );
    const getSequencedRows = createSelector(
        [
            getQuestionnaire,
            getQuestion,
            getCachedDependenciesMentions,
            getRandomizedRows,
            getStaticallySafeEvaluator,
            getEliminationOptions,
            getServerPage,
        ],
        eliminateRows,
    );
    const getSequencedColumns = createSelector(
        [
            getQuestionnaire,
            getQuestion,
            getCachedDependenciesMentions,
            getRandomizedColumns,
            getStaticallySafeEvaluator,
            getEliminationOptions,
            getServerPage,
        ],
        eliminateColumns,
    );

    const getCachedVariables = createDeepEqualSelector(
        getSequencedVariables,
        (variable) => variable,
    );
    const getCachedRows = createDeepEqualSelector(getSequencedRows, (rows) => rows);
    const getCachedColumns = createDeepEqualSelector(getSequencedColumns, (columns) => columns);

    const mapStateToProps = (state: StoreState, props: Props): ControlProps & Props => {
        interviewRef.current = state.interview;
        const { question, onAfterSelection } = props;
        const min: string =
            getSettingString(question, "MinRequiredAnswers") ||
            getSettingString(question, "MinValue");
        const max: string =
            getSettingString(question, "MaxAllowedAnswers") ||
            getSettingString(question, "MaxValue");
        return {
            ...props,
            min: Piping.evaluateRecalls(state.interview, min),
            max: Piping.evaluateRecalls(state.interview, max),
            language: state.interview.language,
            phone: state.interview.respondent.phone,
            email: state.interview.respondent.email,
            timeZone: state.interview.respondent.timeZone,
            block: getCurrentBlock(state.interview.navigation),
            sequencedVariables: getCachedVariables(state, props),
            sequencedRows: getCachedRows(state, props),
            sequencedColumns: getCachedColumns(state, props),
            questionnaire: state.interview.questionnaire,
            mentions: getCachedMentions(state, props),
            matrix: getCurrentLoop(state.interview.navigation),
            showSoftPrompt: props.showSoftPrompt,
            onNavigation: props.onNavigation,
            isDisabled: props.isDisabled,
            theme: state.interview.theme,
            files: getCachedFiles(state, props),
            useClientValidation: state.interview.useClientValidation,
            carrouselActiveVariableIndex: props.carrouselActiveVariableIndex,
            onAfterSelection,
        };
    };
    return mapStateToProps;
};

export const ControlContainer = connect(makeMapStateToProps)(ControlContainerImpl);
